import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchApplicationLatex, updateApplicationFromLatex } from '../api';
import EditableField from '../components/EditableField';
import ChatBot from '../components/ChatBot';
import ResizablePanels from '../components/ResizablePanels';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ResumeViewer = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [application, setApplication] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [selectedBullet, setSelectedBullet] = useState(null); // { section: 'education'|'experience'|'projects', sectionIndex: number, bulletIndex: number }
  const [masterResume, setMasterResume] = useState(null); // Master resume context for assistant

  // Undo/Redo history management
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);
  const [changedFields, setChangedFields] = useState(new Set()); // Track which fields have changed

  // LaTeX editor state
  const [editorMode, setEditorMode] = useState('clean');
  const [latexContent, setLatexContent] = useState('');
  const [latexLoading, setLatexLoading] = useState(false);
  const [latexSaving, setLatexSaving] = useState(false);
  const [latexError, setLatexError] = useState(null);

  // Load master resume for assistant context
  const loadMasterResume = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/resume/download-master`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMasterResume(data);
        console.log('Master resume loaded for assistant context');
      }
    } catch (err) {
      console.error('Failed to load master resume for assistant context:', err);
      // Non-critical error - assistant will still work without master context
    }
  }, [getToken]);

  const loadApplication = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/resume/application/${appId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        const data = await response.json();
        setApplication(data);

        // Load PDF
        const pdfResponse = await fetch(`${API_URL}/resume/download/${appId}`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });

        if (pdfResponse.ok) {
          const blob = await pdfResponse.blob();
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        }
      }
    } catch (err) {
      console.error('Failed to load application:', err);
    } finally {
      setLoading(false);
    }
  }, [appId, getToken]);

  useEffect(() => {
    loadApplication();
  }, [loadApplication]);

  useEffect(() => {
    loadMasterResume();
  }, [loadMasterResume]);

  // Cleanup: Clear history when component unmounts (user exits session)
  useEffect(() => {
    return () => {
      console.log('Clearing undo/redo history on session exit');
      setHistory([]);
      setHistoryIndex(-1);
      setChangedFields(new Set());
    };
  }, []);

  // Track resume_data changes and push to history
  useEffect(() => {
    if (!application?.resume_data) return;

    // Skip if this is an undo/redo action
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }

    // Initialize history with first resume_data
    if (history.length === 0) {
      setHistory([JSON.parse(JSON.stringify(application.resume_data))]);
      setHistoryIndex(0);
      return;
    }

    // Add new state to history, removing any forward history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(application.resume_data)));

    // Limit history to last 50 states to avoid memory issues
    if (newHistory.length > 50) {
      newHistory.shift();
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }

    // Track changed fields by comparing current state with original (first in history)
    if (history.length > 0) {
      const original = history[0];
      const current = application.resume_data;
      const changes = new Set();

      const compareAndTrack = (obj1, obj2, path = '') => {
        if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
          if (JSON.stringify(obj1) !== JSON.stringify(obj2)) {
            changes.add(path);
          }
          return;
        }

        const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);
        allKeys.forEach(key => {
          const newPath = path ? `${path}.${key}` : key;
          compareAndTrack(obj1?.[key], obj2?.[key], newPath);
        });
      };

      compareAndTrack(original, current);
      setChangedFields(changes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application?.resume_data]);

  // PDF reload function (must be defined before undo/redo functions)
  const reloadPDF = useCallback(async () => {
    try {
      console.log('Reloading PDF...');

      // Short delay to ensure backend has completed
      await new Promise(resolve => setTimeout(resolve, 500));

      let retries = 3;
      let previousSize = null;

      for (let attempt = 1; attempt <= retries; attempt++) {
        console.log(`PDF fetch attempt ${attempt}/${retries}`);

        // Force cache bypass with multiple techniques
        const timestamp = Date.now();
        const pdfResponse = await fetch(`${API_URL}/resume/download/${appId}?cachebust=${timestamp}`, {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          cache: 'no-store'
        });

        if (pdfResponse.ok) {
          const blob = await pdfResponse.blob();
          console.log(`PDF blob size: ${blob.size} bytes`);

          // If this is not the first attempt, check if size changed
          if (attempt > 1 && blob.size === previousSize) {
            console.log('PDF size unchanged, retrying...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }

          console.log('PDF fetch successful');

          // Revoke old URL to prevent memory leaks
          if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
          }

          // Add timestamp to blob URL to force iframe refresh
          const url = URL.createObjectURL(blob) + `#${timestamp}`;
          setPdfUrl(url);
          console.log('PDF URL updated:', url);
          return; // Success, exit retry loop
        } else {
          const errorText = await pdfResponse.text();
          console.error(`Failed to fetch PDF (attempt ${attempt}):`, pdfResponse.status, errorText);

          if (attempt === retries) {
            alert(`Failed to reload PDF: ${pdfResponse.status}`);
          } else {
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        previousSize = null; // Reset for next attempt
      }
    } catch (err) {
      console.error('Failed to reload PDF:', err);
      alert(`Failed to reload PDF: ${err.message}`);
    }
  }, [appId, getToken, pdfUrl]);

  // Undo function
  const handleUndo = useCallback(async () => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true;
      const previousState = history[historyIndex - 1];
      setApplication(prev => ({
        ...prev,
        resume_data: JSON.parse(JSON.stringify(previousState))
      }));
      setHistoryIndex(historyIndex - 1);

      // Trigger PDF regeneration
      setIsRegenerating(true);
      try {
        const response = await fetch(`${API_URL}/resume/application/${appId}/resume`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(previousState)
        });

        if (response.ok) {
          await reloadPDF();
        }
      } catch (err) {
        console.error('Error updating resume after undo:', err);
      } finally {
        setIsRegenerating(false);
      }
    }
  }, [historyIndex, history, appId, getToken, reloadPDF]);

  // Redo function
  const handleRedo = useCallback(async () => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      const nextState = history[historyIndex + 1];
      setApplication(prev => ({
        ...prev,
        resume_data: JSON.parse(JSON.stringify(nextState))
      }));
      setHistoryIndex(historyIndex + 1);

      // Trigger PDF regeneration
      setIsRegenerating(true);
      try {
        const response = await fetch(`${API_URL}/resume/application/${appId}/resume`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(nextState)
        });

        if (response.ok) {
          await reloadPDF();
        }
      } catch (err) {
        console.error('Error updating resume after redo:', err);
      } finally {
        setIsRegenerating(false);
      }
    }
  }, [historyIndex, history, appId, getToken, reloadPDF]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Ctrl+Z (or Cmd+Z on Mac) for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Check for Ctrl+Shift+Z (or Cmd+Shift+Z on Mac) for redo
      else if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
      // Alternative: Ctrl+Y (or Cmd+Y) for redo
      else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Click outside to deselect bullet
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is outside editable fields and chatbot
      const isEditableField = e.target.closest('[data-editable-field]');
      const isChatbot = e.target.closest('[data-chatbot]');

      if (!isEditableField && !isChatbot && selectedBullet) {
        setSelectedBullet(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedBullet]);

  const handleDownload = async () => {
    if (!application) return;

    try {
      const response = await fetch(`${API_URL}/resume/download/${appId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        // Format: FirstName_LastName_Company_JobTitle.pdf
        const sanitize = (str) => str.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
        const name = application.resume_data?.contact_info?.full_name || 'Resume';
        const company = application.company_name || 'Company';
        const jobRole = application.job_role || 'Position';
        const truncatedRole = jobRole.length > 50 ? (jobRole.substring(0, 50).replace(/\s+\S*$/, '') || jobRole.substring(0, 50)) : jobRole;
        const fileName = `${sanitize(name)}_${sanitize(company)}_${sanitize(truncatedRole)}.pdf`;

        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download resume');
    }
  };

  // Load LaTeX content when switching to raw mode
  const loadLatexContent = useCallback(async () => {
    try {
      setLatexLoading(true);
      setLatexError(null);
      const data = await fetchApplicationLatex(appId, getToken());
      setLatexContent(data.latex_content);
    } catch (err) {
      console.error('Failed to load LaTeX:', err);
      setLatexError(err.message || 'Failed to load LaTeX content');
    } finally {
      setLatexLoading(false);
    }
  }, [appId, getToken]);

  // Handle editor mode switch
  const handleEditorModeChange = async (mode) => {
    if (mode === 'raw' && editorMode !== 'raw') {
      // Switching to raw mode - load LaTeX
      await loadLatexContent();
    } else if (mode === 'clean' && editorMode !== 'clean') {
      // Switching back to clean mode - reload resume data (in case LaTeX was saved)
      await loadApplication();
    }
    setEditorMode(mode);
  };

  // Save LaTeX content
  const saveLatexContent = async () => {
    try {
      setLatexSaving(true);
      setLatexError(null);
      await updateApplicationFromLatex(appId, latexContent, getToken());
      await reloadPDF();
    } catch (err) {
      console.error('Failed to save LaTeX:', err);
      setLatexError(err.response?.data?.detail || err.message || 'Failed to save LaTeX. Check your syntax.');
    } finally {
      setLatexSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const updateResumeField = useCallback(async (path, value) => {
    if (!application?.resume_data) return;

    // Update local state
    const updatedData = { ...application.resume_data };
    let current = updatedData;
    const keys = path.split('.');

    // Navigate to the nested property
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const match = key.match(/(.+)\[(\d+)\]/);
      if (match) {
        const arrayKey = match[1];
        const index = parseInt(match[2]);
        current = current[arrayKey][index];
      } else {
        current = current[key];
      }
    }

    // Set the value
    const lastKey = keys[keys.length - 1];
    const lastMatch = lastKey.match(/(.+)\[(\d+)\]/);
    if (lastMatch) {
      const arrayKey = lastMatch[1];
      const index = parseInt(lastMatch[2]);
      current[arrayKey][index] = value;
    } else {
      current[lastKey] = value;
    }

    // Update application state
    setApplication(prev => ({
      ...prev,
      resume_data: updatedData
    }));

    // Send update to backend
    setIsRegenerating(true);
    try {
      const response = await fetch(`${API_URL}/resume/application/${appId}/resume`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Update successful:', result);
        // Reload PDF
        await reloadPDF();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to update resume:', errorData);
        alert(`Failed to update resume: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating resume:', err);
      alert(`Error updating resume: ${err.message}`);
    } finally {
      setIsRegenerating(false);
    }
  }, [application, appId, getToken, reloadPDF]);

  const handleBulletUpdate = useCallback((bulletInfo, newText) => {
    if (!bulletInfo) return;

    const { section, sectionIndex, bulletIndex } = bulletInfo;

    // Build the path based on section type
    let path;
    if (section === 'education') {
      path = `education[${sectionIndex}].bullets[${bulletIndex}]`;
    } else if (section === 'experience') {
      path = `experience[${sectionIndex}].bullets[${bulletIndex}]`;
    } else if (section === 'projects' || section === 'project') {
      path = `projects[${sectionIndex}].bullets[${bulletIndex}]`;
    }

    if (path) {
      updateResumeField(path, newText);
    }
  }, [updateResumeField]);

  // Helper function to check if a field has changed
  const isFieldChanged = useCallback((path) => {
    return changedFields.has(path);
  }, [changedFields]);

  const addBulletPoint = useCallback(async (path) => {
    if (!application?.resume_data) return;

    // Update local state
    const updatedData = { ...application.resume_data };
    let current = updatedData;
    const keys = path.split('.');

    // Navigate to the array
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const match = key.match(/(.+)\[(\d+)\]/);
      if (match) {
        const arrayKey = match[1];
        const index = parseInt(match[2]);
        current = current[arrayKey][index];
      } else {
        current = current[key];
      }
    }

    // Add new bullet to the array
    const lastKey = keys[keys.length - 1];
    if (!current[lastKey]) {
      current[lastKey] = [];
    }
    current[lastKey].push('New bullet point');

    // Update application state
    setApplication(prev => ({
      ...prev,
      resume_data: updatedData
    }));

    // Send update to backend
    setIsRegenerating(true);
    try {
      const response = await fetch(`${API_URL}/resume/application/${appId}/resume`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Bullet point added:', result);
        await reloadPDF();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to add bullet point:', errorData);
        alert(`Failed to add bullet point: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error adding bullet point:', err);
      alert(`Error adding bullet point: ${err.message}`);
    } finally {
      setIsRegenerating(false);
    }
  }, [application, appId, getToken, reloadPDF]);

  const deleteBulletPoint = useCallback(async (path, index) => {
    if (!application?.resume_data) return;

    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this bullet point?')) {
      return;
    }

    // Update local state
    const updatedData = { ...application.resume_data };
    let current = updatedData;
    const keys = path.split('.');

    // Navigate to the array
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const match = key.match(/(.+)\[(\d+)\]/);
      if (match) {
        const arrayKey = match[1];
        const arrayIndex = parseInt(match[2]);
        current = current[arrayKey][arrayIndex];
      } else {
        current = current[key];
      }
    }

    // Remove the bullet from the array
    const lastKey = keys[keys.length - 1];
    if (current[lastKey] && Array.isArray(current[lastKey])) {
      current[lastKey].splice(index, 1);
    }

    // Update application state
    setApplication(prev => ({
      ...prev,
      resume_data: updatedData
    }));

    // Send update to backend
    setIsRegenerating(true);
    try {
      const response = await fetch(`${API_URL}/resume/application/${appId}/resume`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Bullet point deleted:', result);
        await reloadPDF();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to delete bullet point:', errorData);
        alert(`Failed to delete bullet point: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting bullet point:', err);
      alert(`Error deleting bullet point: ${err.message}`);
    } finally {
      setIsRegenerating(false);
    }
  }, [application, appId, getToken, reloadPDF]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-base)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-primary)'
      }}>
        <div>Loading resume...</div>
      </div>
    );
  }

  if (!application) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-base)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-primary)',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div>Resume not found</div>
        <button onClick={() => navigate('/dashboard')} style={{
          padding: '12px 24px',
          background: 'var(--gradient-primary)',
          color: 'var(--text-primary)',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: '600'
        }}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const resumeData = application.resume_data || {};

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-base)',
      color: 'var(--text-primary)'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 40px',
        borderBottom: '1px solid var(--border-light)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'var(--bg-surface)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '10px 20px',
              background: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-light)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← Back to Dashboard
          </button>
          <div>
            <h1 style={{ fontSize: '24px', margin: 0 }}>{application.company_name} — {application.job_role}</h1>
            <p style={{ color: 'var(--text-tertiary)', margin: '4px 0 0 0', fontSize: '14px' }}>
              Created: {new Date(application.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowChatbot(!showChatbot)}
            style={{
              padding: '12px 24px',
              background: showChatbot
                ? 'var(--gradient-primary)'
                : 'rgba(99, 102, 241, 0.15)',
              color: showChatbot ? '#fff' : 'var(--indigo-500)',
              border: showChatbot ? 'none' : '1px solid rgba(99, 102, 241, 0.5)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px',
              boxShadow: showChatbot ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            {showChatbot ? '✓ AI Assistant' : '🤖 Toggle AI Assistant'}
          </button>
          <button
            onClick={handleDownload}
            style={{
              padding: '12px 24px',
              background: 'var(--gradient-primary)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
            }}
          >
            📥 Download Resume
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', height: 'calc(100vh - 100px)' }}>
        {!showChatbot ? (
          <>
            {/* Left Sidebar - Resume Sections */}
            <div style={{
              width: '40%',
              overflowY: 'auto',
              padding: '30px',
              borderRight: '1px solid var(--border-light)'
            }}>
              {/* Undo/Redo Buttons */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '20px',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}>
                <button
                  onClick={handleUndo}
                  disabled={historyIndex <= 0 || history.length === 0}
                  title="Undo (Ctrl+Z)"
                  style={{
                    padding: '8px 16px',
                    background: historyIndex > 0 && history.length > 0
                      ? 'rgba(99, 102, 241, 0.15)'
                      : 'var(--bg-surface)',
                    border: '1px solid',
                    borderColor: historyIndex > 0 && history.length > 0
                      ? 'rgba(99, 102, 241, 0.3)'
                      : 'var(--border-light)',
                    color: historyIndex > 0 && history.length > 0 ? 'var(--indigo-500)' : 'var(--text-muted)',
                    borderRadius: '8px',
                    cursor: historyIndex > 0 && history.length > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: '600',
                    fontSize: '13px',
                    transition: 'all 0.2s'
                  }}
                >
                  ↶ Undo
                </button>
                <button
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1 || history.length === 0}
                  title="Redo (Ctrl+Shift+Z or Ctrl+Y)"
                  style={{
                    padding: '8px 16px',
                    background: historyIndex < history.length - 1 && history.length > 0
                      ? 'rgba(99, 102, 241, 0.15)'
                      : 'var(--bg-surface)',
                    border: '1px solid',
                    borderColor: historyIndex < history.length - 1 && history.length > 0
                      ? 'rgba(99, 102, 241, 0.3)'
                      : 'var(--border-light)',
                    color: historyIndex < history.length - 1 && history.length > 0 ? 'var(--indigo-500)' : 'var(--text-muted)',
                    borderRadius: '8px',
                    cursor: historyIndex < history.length - 1 && history.length > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: '600',
                    fontSize: '13px',
                    transition: 'all 0.2s'
                  }}
                >
                  ↷ Redo
                </button>

                {/* Editor Mode Toggle */}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleEditorModeChange('clean')}
                    style={{
                      padding: '8px 16px',
                      background: editorMode === 'clean' ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                      border: '1px solid rgba(99, 102, 241, 0.5)',
                      borderRadius: '6px',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontWeight: editorMode === 'clean' ? '600' : '400',
                      fontSize: '13px'
                    }}
                  >
                    Clean Editor
                  </button>
                  <button
                    onClick={() => handleEditorModeChange('raw')}
                    style={{
                      padding: '8px 16px',
                      background: editorMode === 'raw' ? 'rgba(245, 158, 11, 0.3)' : 'transparent',
                      border: '1px solid rgba(245, 158, 11, 0.5)',
                      borderRadius: '6px',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontWeight: editorMode === 'raw' ? '600' : '400',
                      fontSize: '13px'
                    }}
                  >
                    Raw LaTeX
                  </button>
                </div>
              </div>

              {editorMode === 'raw' ? (
                /* LaTeX Editor */
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Raw LaTeX Editor</h2>
                    <button
                      onClick={saveLatexContent}
                      disabled={latexSaving}
                      style={{
                        padding: '8px 20px',
                        background: latexSaving ? 'rgba(99, 102, 241, 0.3)' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        border: 'none',
                        borderRadius: '6px',
                        color: 'white',
                        cursor: latexSaving ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                    >
                      {latexSaving ? 'Compiling...' : 'Compile & Save'}
                    </button>
                  </div>

                  {latexError && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.4)',
                      borderRadius: '8px',
                      color: '#f87171',
                      marginBottom: '12px',
                      fontSize: '13px'
                    }}>
                      {latexError}
                    </div>
                  )}

                  {latexLoading ? (
                    <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-tertiary)' }}>
                      Loading LaTeX content...
                    </div>
                  ) : (
                    <textarea
                      value={latexContent}
                      onChange={(e) => setLatexContent(e.target.value)}
                      style={{
                        width: '100%',
                        height: 'calc(100vh - 280px)',
                        padding: '16px',
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--border-light)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        lineHeight: '1.5',
                        resize: 'none'
                      }}
                      spellCheck={false}
                    />
                  )}

                  <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    Edit the raw LaTeX code directly. Changes will be parsed back to structured data when saved.
                  </p>
                </div>
              ) : (
                /* Clean Editor - Original content */
                <>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', fontWeight: '700' }}>Resume Sections</h2>

          {/* Personal Information */}
          <Section
            title="Personal Information"
            icon="👤"
            isExpanded={expandedSection === 'personal'}
            onToggle={() => toggleSection('personal')}
          >
            {resumeData.contact_info && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <InfoRow
                  label="Name"
                  value={resumeData.contact_info.full_name}
                  editable
                  onSave={(val) => updateResumeField('contact_info.full_name', val)}
                />

                <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '12px', fontWeight: '600' }}>
                    CONTACT VISIBILITY (toggle to hide from PDF)
                  </div>

                  <FieldWithVisibility
                    label="Email"
                    value={resumeData.contact_info.email || ''}
                    visible={resumeData.contact_info.visibility?.email !== false}
                    onValueChange={(v) => updateResumeField('contact_info.email', v)}
                    onVisibilityChange={(v) => {
                      const newVisibility = { ...(resumeData.contact_info.visibility || {}), email: v };
                      updateResumeField('contact_info.visibility', newVisibility);
                    }}
                  />

                  <FieldWithVisibility
                    label="Phone"
                    value={resumeData.contact_info.phone || ''}
                    visible={resumeData.contact_info.visibility?.phone !== false}
                    onValueChange={(v) => updateResumeField('contact_info.phone', v)}
                    onVisibilityChange={(v) => {
                      const newVisibility = { ...(resumeData.contact_info.visibility || {}), phone: v };
                      updateResumeField('contact_info.visibility', newVisibility);
                    }}
                  />

                  <FieldWithVisibility
                    label="LinkedIn URL"
                    value={resumeData.contact_info.linkedin_url || ''}
                    visible={resumeData.contact_info.visibility?.linkedin !== false}
                    onValueChange={(v) => updateResumeField('contact_info.linkedin_url', v)}
                    onVisibilityChange={(v) => {
                      const newVisibility = { ...(resumeData.contact_info.visibility || {}), linkedin: v };
                      updateResumeField('contact_info.visibility', newVisibility);
                    }}
                    isLink
                  />

                  <FieldWithVisibility
                    label="GitHub URL"
                    value={resumeData.contact_info.github_url || ''}
                    visible={resumeData.contact_info.visibility?.github !== false}
                    onValueChange={(v) => updateResumeField('contact_info.github_url', v)}
                    onVisibilityChange={(v) => {
                      const newVisibility = { ...(resumeData.contact_info.visibility || {}), github: v };
                      updateResumeField('contact_info.visibility', newVisibility);
                    }}
                    isLink
                  />

                  <FieldWithVisibility
                    label="Portfolio URL"
                    value={resumeData.contact_info.portfolio_url || ''}
                    visible={resumeData.contact_info.visibility?.portfolio !== false}
                    onValueChange={(v) => updateResumeField('contact_info.portfolio_url', v)}
                    onVisibilityChange={(v) => {
                      const newVisibility = { ...(resumeData.contact_info.visibility || {}), portfolio: v };
                      updateResumeField('contact_info.visibility', newVisibility);
                    }}
                    isLink
                  />
                </div>
              </div>
            )}
          </Section>

          {/* Education */}
          <Section
            title="Education"
            icon="🎓"
            isExpanded={expandedSection === 'education'}
            onToggle={() => toggleSection('education')}
          >
            {resumeData.education?.map((edu, index) => (
              <div key={index} style={{
                marginBottom: index < resumeData.education.length - 1 ? '20px' : 0,
                paddingBottom: index < resumeData.education.length - 1 ? '20px' : 0,
                borderBottom: index < resumeData.education.length - 1 ? '1px solid var(--border-light)' : 'none'
              }}>
                {/* University */}
                {edu.school_name && (
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                    <EditableField
                      value={edu.school_name}
                      onSave={(val) => updateResumeField(`education[${index}].school_name`, val)}
                    />
                  </div>
                )}

                {/* Degree */}
                {edu.degree && (
                  <InfoRow
                    label="Degree"
                    value={edu.degree}
                    editable
                    onSave={(val) => updateResumeField(`education[${index}].degree`, val)}
                  />
                )}

                {/* GPA */}
                {edu.gpa && (
                  <InfoRow
                    label="GPA"
                    value={edu.gpa}
                    editable
                    onSave={(val) => updateResumeField(`education[${index}].gpa`, val)}
                  />
                )}

                {/* Major */}
                {edu.major && (
                  <InfoRow
                    label="Major"
                    value={edu.major}
                    editable
                    onSave={(val) => updateResumeField(`education[${index}].major`, val)}
                  />
                )}

                {/* Minor */}
                {edu.minor && (
                  <InfoRow
                    label="Minor"
                    value={edu.minor}
                    editable
                    onSave={(val) => updateResumeField(`education[${index}].minor`, val)}
                  />
                )}

                {/* Graduation Date */}
                {edu.graduation_date && (
                  <InfoRow
                    label="Graduation Date"
                    value={edu.graduation_date}
                    editable
                    onSave={(val) => updateResumeField(`education[${index}].graduation_date`, val)}
                  />
                )}

                {/* Bullet Points (Honors, Clubs, Coursework, etc.) */}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>Honors/Achievements:</div>
                  {edu.bullets && edu.bullets.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.6' }}>
                      {edu.bullets.map((bullet, i) => {
                        const isSelected = selectedBullet?.section === 'education' &&
                                          selectedBullet?.sectionIndex === index &&
                                          selectedBullet?.bulletIndex === i;
                        const bulletPath = `education.${index}.bullets.${i}`;
                        return (
                        <li key={i} style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <EditableField
                              value={bullet}
                              onSave={(val) => updateResumeField(`education[${index}].bullets[${i}]`, val)}
                              isMultiline={true}
                              isSelected={isSelected}
                              onSelect={() => setSelectedBullet({ section: 'education', sectionIndex: index, bulletIndex: i })}
                              isChanged={isFieldChanged(bulletPath)}
                            />
                          </div>
                          <button
                            onClick={() => deleteBulletPoint(`education[${index}].bullets`, i)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              color: 'var(--red-500)',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              marginTop: '2px'
                            }}
                            title="Delete bullet point"
                          >
                            ✕
                          </button>
                        </li>
                        );
                      })}
                    </ul>
                  )}
                  <button
                    onClick={() => addBulletPoint(`education[${index}].bullets`)}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      background: 'rgba(147, 51, 234, 0.1)',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      color: 'var(--indigo-500)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      width: '100%'
                    }}
                  >
                    + Add Bullet Point
                  </button>
                </div>
              </div>
            ))}
          </Section>

          {/* Experience */}
          <Section
            title="Experience"
            icon="💼"
            isExpanded={expandedSection === 'experience'}
            onToggle={() => toggleSection('experience')}
          >
            {resumeData.experience?.map((exp, index) => (
              <div key={index} style={{
                marginBottom: index < resumeData.experience.length - 1 ? '20px' : 0,
                paddingBottom: index < resumeData.experience.length - 1 ? '20px' : 0,
                borderBottom: index < resumeData.experience.length - 1 ? '1px solid var(--border-light)' : 'none'
              }}>
                {/* Job Title */}
                {exp.job_title && (
                  <InfoRow
                    label="Job Title"
                    value={exp.job_title}
                    editable
                    onSave={(val) => updateResumeField(`experience[${index}].job_title`, val)}
                  />
                )}

                {/* Company */}
                {exp.company_name && (
                  <InfoRow
                    label="Company"
                    value={exp.company_name}
                    editable
                    onSave={(val) => updateResumeField(`experience[${index}].company_name`, val)}
                  />
                )}

                {/* Location */}
                {exp.location && (
                  <InfoRow
                    label="Location"
                    value={exp.location}
                    editable
                    onSave={(val) => updateResumeField(`experience[${index}].location`, val)}
                  />
                )}

                {/* Start Date */}
                {exp.start_date && (
                  <InfoRow
                    label="Start Date"
                    value={exp.start_date}
                    editable
                    onSave={(val) => updateResumeField(`experience[${index}].start_date`, val)}
                  />
                )}

                {/* End Date */}
                {exp.end_date && (
                  <InfoRow
                    label="End Date"
                    value={exp.end_date}
                    editable
                    onSave={(val) => updateResumeField(`experience[${index}].end_date`, val)}
                  />
                )}

                {/* Bullet Points */}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>Bullet Points:</div>
                  {exp.bullets && exp.bullets.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.6' }}>
                      {exp.bullets.map((bullet, i) => {
                        const isSelected = selectedBullet?.section === 'experience' &&
                                          selectedBullet?.sectionIndex === index &&
                                          selectedBullet?.bulletIndex === i;
                        const bulletPath = `experience.${index}.bullets.${i}`;
                        return (
                        <li key={i} style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <EditableField
                              value={bullet}
                              onSave={(val) => updateResumeField(`experience[${index}].bullets[${i}]`, val)}
                              isMultiline={true}
                              isSelected={isSelected}
                              onSelect={() => setSelectedBullet({ section: 'experience', sectionIndex: index, bulletIndex: i })}
                              isChanged={isFieldChanged(bulletPath)}
                            />
                          </div>
                          <button
                            onClick={() => deleteBulletPoint(`experience[${index}].bullets`, i)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              color: 'var(--red-500)',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              marginTop: '2px'
                            }}
                            title="Delete bullet point"
                          >
                            ✕
                          </button>
                        </li>
                        );
                      })}
                    </ul>
                  )}
                  <button
                    onClick={() => addBulletPoint(`experience[${index}].bullets`)}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      background: 'rgba(147, 51, 234, 0.1)',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      color: 'var(--indigo-500)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      width: '100%'
                    }}
                  >
                    + Add Bullet Point
                  </button>
                </div>
              </div>
            ))}
          </Section>

          {/* Projects */}
          <Section
            title="Projects"
            icon="🚀"
            isExpanded={expandedSection === 'projects'}
            onToggle={() => toggleSection('projects')}
          >
            {resumeData.projects?.map((proj, index) => (
              <div key={index} style={{
                marginBottom: index < resumeData.projects.length - 1 ? '20px' : 0,
                paddingBottom: index < resumeData.projects.length - 1 ? '20px' : 0,
                borderBottom: index < resumeData.projects.length - 1 ? '1px solid var(--border-light)' : 'none'
              }}>
                {/* Project Name */}
                {proj.project_name && (
                  <InfoRow
                    label="Project Name"
                    value={proj.project_name}
                    editable
                    onSave={(val) => updateResumeField(`projects[${index}].project_name`, val)}
                  />
                )}

                {/* Context */}
                {proj.context && (
                  <InfoRow
                    label="Context"
                    value={proj.context}
                    editable
                    onSave={(val) => updateResumeField(`projects[${index}].context`, val)}
                  />
                )}

                {/* Date */}
                {proj.date && (
                  <InfoRow
                    label="Date"
                    value={proj.date}
                    editable
                    onSave={(val) => updateResumeField(`projects[${index}].date`, val)}
                  />
                )}

                {/* GitHub */}
                {proj.github_link && (
                  <InfoRow
                    label="GitHub"
                    value={proj.github_link}
                    isLink
                    editable
                    onSave={(val) => updateResumeField(`projects[${index}].github_link`, val)}
                  />
                )}

                {/* Demo */}
                {proj.demo_link && (
                  <InfoRow
                    label="Demo"
                    value={proj.demo_link}
                    isLink
                    editable
                    onSave={(val) => updateResumeField(`projects[${index}].demo_link`, val)}
                  />
                )}

                {/* Bullet Points */}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>Bullet Points:</div>
                  {proj.bullets && proj.bullets.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.6' }}>
                      {proj.bullets.map((bullet, i) => {
                        const isSelected = selectedBullet?.section === 'projects' &&
                                          selectedBullet?.sectionIndex === index &&
                                          selectedBullet?.bulletIndex === i;
                        const bulletPath = `projects.${index}.bullets.${i}`;
                        return (
                        <li key={i} style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <EditableField
                              value={bullet}
                              onSave={(val) => updateResumeField(`projects[${index}].bullets[${i}]`, val)}
                              isMultiline={true}
                              isSelected={isSelected}
                              onSelect={() => setSelectedBullet({ section: 'projects', sectionIndex: index, bulletIndex: i })}
                              isChanged={isFieldChanged(bulletPath)}
                            />
                          </div>
                          <button
                            onClick={() => deleteBulletPoint(`projects[${index}].bullets`, i)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              color: 'var(--red-500)',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              marginTop: '2px'
                            }}
                            title="Delete bullet point"
                          >
                            ✕
                          </button>
                        </li>
                        );
                      })}
                    </ul>
                  )}
                  <button
                    onClick={() => addBulletPoint(`projects[${index}].bullets`)}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      background: 'rgba(147, 51, 234, 0.1)',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      color: 'var(--indigo-500)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      width: '100%'
                    }}
                  >
                    + Add Bullet Point
                  </button>
                </div>
              </div>
            ))}
          </Section>

          {/* Skills */}
          <Section
            title="Skills"
            icon="⚡"
            isExpanded={expandedSection === 'skills'}
            onToggle={() => toggleSection('skills')}
          >
            {resumeData.skills && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {resumeData.skills.languages?.length > 0 && (
                  <InfoRow
                    label="Languages"
                    value={Array.isArray(resumeData.skills.languages) ? resumeData.skills.languages.join(', ') : resumeData.skills.languages}
                    editable
                    onSave={(val) => updateResumeField('skills.languages', val.split(',').map(s => s.trim()))}
                  />
                )}
                {resumeData.skills.frontend?.length > 0 && (
                  <InfoRow
                    label="Frontend"
                    value={Array.isArray(resumeData.skills.frontend) ? resumeData.skills.frontend.join(', ') : resumeData.skills.frontend}
                    editable
                    onSave={(val) => updateResumeField('skills.frontend', val.split(',').map(s => s.trim()))}
                  />
                )}
                {resumeData.skills.backend?.length > 0 && (
                  <InfoRow
                    label="Backend"
                    value={Array.isArray(resumeData.skills.backend) ? resumeData.skills.backend.join(', ') : resumeData.skills.backend}
                    editable
                    onSave={(val) => updateResumeField('skills.backend', val.split(',').map(s => s.trim()))}
                  />
                )}
                {resumeData.skills.databases?.length > 0 && (
                  <InfoRow
                    label="Databases"
                    value={Array.isArray(resumeData.skills.databases) ? resumeData.skills.databases.join(', ') : resumeData.skills.databases}
                    editable
                    onSave={(val) => updateResumeField('skills.databases', val.split(',').map(s => s.trim()))}
                  />
                )}
                {resumeData.skills.cloud_devops?.length > 0 && (
                  <InfoRow
                    label="Cloud & DevOps"
                    value={Array.isArray(resumeData.skills.cloud_devops) ? resumeData.skills.cloud_devops.join(', ') : resumeData.skills.cloud_devops}
                    editable
                    onSave={(val) => updateResumeField('skills.cloud_devops', val.split(',').map(s => s.trim()))}
                  />
                )}
                {resumeData.skills.tools?.length > 0 && (
                  <InfoRow
                    label="Tools"
                    value={Array.isArray(resumeData.skills.tools) ? resumeData.skills.tools.join(', ') : resumeData.skills.tools}
                    editable
                    onSave={(val) => updateResumeField('skills.tools', val.split(',').map(s => s.trim()))}
                  />
                )}
                {resumeData.skills.other?.length > 0 && (
                  <InfoRow
                    label="Other"
                    value={Array.isArray(resumeData.skills.other) ? resumeData.skills.other.join(', ') : resumeData.skills.other}
                    editable
                    onSave={(val) => updateResumeField('skills.other', val.split(',').map(s => s.trim()))}
                  />
                )}
              </div>
            )}
          </Section>

          {/* Additional Information */}
          <Section
            title="Additional Information"
            icon="📋"
            isExpanded={expandedSection === 'additional'}
            onToggle={() => toggleSection('additional')}
          >
            {resumeData.certifications?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: '600', marginBottom: '12px' }}>Certifications</div>
                {resumeData.certifications.map((cert, index) => (
                  <div key={index} style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '14px' }}>{cert.cert_name}</div>
                    <InfoRow label="Issuer" value={cert.issuing_org} />
                    <InfoRow label="Date" value={cert.date_obtained} />
                    {cert.expiration_date && <InfoRow label="Expires" value={cert.expiration_date} />}
                  </div>
                ))}
              </div>
            )}

            {resumeData.honors?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: '600', marginBottom: '12px' }}>Honors & Awards</div>
                {resumeData.honors.map((honor, index) => (
                  <div key={index} style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '14px' }}>{honor.award_name}</div>
                    <InfoRow label="Organization" value={honor.issuing_org} />
                    <InfoRow label="Date" value={honor.date} />
                    {honor.description && <InfoRow label="Description" value={honor.description} />}
                  </div>
                ))}
              </div>
            )}

            {resumeData.leadership?.length > 0 && (
              <div>
                <div style={{ fontWeight: '600', marginBottom: '12px' }}>Leadership & Extracurriculars</div>
                {resumeData.leadership.map((lead, index) => (
                  <div key={index} style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '14px' }}>{lead.role} - {lead.organization}</div>
                    <InfoRow label="Duration" value={lead.dates} />
                    {lead.achievements?.length > 0 && (
                      <InfoRow label="Achievements" value={lead.achievements.join(', ')} />
                    )}
                  </div>
                ))}
              </div>
            )}

            {(!resumeData.certifications?.length && !resumeData.honors?.length && !resumeData.leadership?.length) && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>No additional information</div>
            )}
          </Section>
                </>
              )}
        </div>

        {/* Right Side - PDF Viewer */}
        <div style={{
          width: '60%',
          background: 'var(--bg-elevated)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          position: 'relative'
        }}>
          {pdfUrl ? (
            <>
              <iframe
                key={pdfUrl}
                src={pdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                  opacity: isRegenerating ? 0.3 : 1,
                  transition: 'opacity 0.3s'
                }}
                title="Resume PDF"
              />
              {isRegenerating && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(0, 0, 0, 0.8)',
                  padding: '24px 32px',
                  borderRadius: '12px',
                  border: '1px solid var(--indigo-500)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: '0 8px 32px rgba(147, 51, 234, 0.4)'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid rgba(99, 102, 241, 0.3)',
                    borderTop: '4px solid var(--indigo-500)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '600' }}>
                    Regenerating Resume...
                  </div>
                  <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
                    Compiling LaTeX to PDF
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ color: 'var(--text-tertiary)' }}>Loading PDF...</div>
          )}
        </div>
          </>
        ) : (
          /* With Chatbot - 34/33/33 split */
          <ResizablePanels
            defaultSizes={[34, 33, 33]}
            onReset={() => setShowChatbot(false)}
          >
            {/* Left Panel - Resume Sections */}
            <div style={{
              height: '100%',
              overflowY: 'auto',
              padding: '30px',
              background: 'var(--bg-base)'
            }}>
              {/* Undo/Redo Buttons */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '20px',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}>
                <button
                  onClick={handleUndo}
                  disabled={historyIndex <= 0 || history.length === 0}
                  title="Undo (Ctrl+Z)"
                  style={{
                    padding: '8px 16px',
                    background: historyIndex > 0 && history.length > 0
                      ? 'rgba(99, 102, 241, 0.15)'
                      : 'var(--bg-surface)',
                    border: '1px solid',
                    borderColor: historyIndex > 0 && history.length > 0
                      ? 'rgba(99, 102, 241, 0.3)'
                      : 'var(--border-light)',
                    color: historyIndex > 0 && history.length > 0 ? 'var(--indigo-500)' : 'var(--text-muted)',
                    borderRadius: '8px',
                    cursor: historyIndex > 0 && history.length > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: '600',
                    fontSize: '13px',
                    transition: 'all 0.2s'
                  }}
                >
                  ↶ Undo
                </button>
                <button
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1 || history.length === 0}
                  title="Redo (Ctrl+Shift+Z or Ctrl+Y)"
                  style={{
                    padding: '8px 16px',
                    background: historyIndex < history.length - 1 && history.length > 0
                      ? 'rgba(99, 102, 241, 0.15)'
                      : 'var(--bg-surface)',
                    border: '1px solid',
                    borderColor: historyIndex < history.length - 1 && history.length > 0
                      ? 'rgba(99, 102, 241, 0.3)'
                      : 'var(--border-light)',
                    color: historyIndex < history.length - 1 && history.length > 0 ? 'var(--indigo-500)' : 'var(--text-muted)',
                    borderRadius: '8px',
                    cursor: historyIndex < history.length - 1 && history.length > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: '600',
                    fontSize: '13px',
                    transition: 'all 0.2s'
                  }}
                >
                  ↷ Redo
                </button>
              </div>

              <h2 style={{ fontSize: '20px', marginBottom: '20px', fontWeight: '700' }}>Resume Sections</h2>
              {/* Personal Information */}
              <Section
                title="Personal Information"
                icon="👤"
                isExpanded={expandedSection === 'personal'}
                onToggle={() => toggleSection('personal')}
              >
                {resumeData.contact_info && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <InfoRow
                      label="Name"
                      value={resumeData.contact_info.full_name}
                      editable
                      onSave={(val) => updateResumeField('contact_info.full_name', val)}
                      isChanged={isFieldChanged('contact_info.full_name')}
                    />

                    <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '12px', fontWeight: '600' }}>
                        CONTACT VISIBILITY (toggle to hide from PDF)
                      </div>

                      <FieldWithVisibility
                        label="Email"
                        value={resumeData.contact_info.email || ''}
                        visible={resumeData.contact_info.visibility?.email !== false}
                        onValueChange={(v) => updateResumeField('contact_info.email', v)}
                        onVisibilityChange={(v) => {
                          const newVisibility = { ...(resumeData.contact_info.visibility || {}), email: v };
                          updateResumeField('contact_info.visibility', newVisibility);
                        }}
                      />

                      <FieldWithVisibility
                        label="Phone"
                        value={resumeData.contact_info.phone || ''}
                        visible={resumeData.contact_info.visibility?.phone !== false}
                        onValueChange={(v) => updateResumeField('contact_info.phone', v)}
                        onVisibilityChange={(v) => {
                          const newVisibility = { ...(resumeData.contact_info.visibility || {}), phone: v };
                          updateResumeField('contact_info.visibility', newVisibility);
                        }}
                      />

                      <FieldWithVisibility
                        label="LinkedIn URL"
                        value={resumeData.contact_info.linkedin_url || ''}
                        visible={resumeData.contact_info.visibility?.linkedin !== false}
                        onValueChange={(v) => updateResumeField('contact_info.linkedin_url', v)}
                        onVisibilityChange={(v) => {
                          const newVisibility = { ...(resumeData.contact_info.visibility || {}), linkedin: v };
                          updateResumeField('contact_info.visibility', newVisibility);
                        }}
                        isLink
                      />

                      <FieldWithVisibility
                        label="GitHub URL"
                        value={resumeData.contact_info.github_url || ''}
                        visible={resumeData.contact_info.visibility?.github !== false}
                        onValueChange={(v) => updateResumeField('contact_info.github_url', v)}
                        onVisibilityChange={(v) => {
                          const newVisibility = { ...(resumeData.contact_info.visibility || {}), github: v };
                          updateResumeField('contact_info.visibility', newVisibility);
                        }}
                        isLink
                      />

                      <FieldWithVisibility
                        label="Portfolio URL"
                        value={resumeData.contact_info.portfolio_url || ''}
                        visible={resumeData.contact_info.visibility?.portfolio !== false}
                        onValueChange={(v) => updateResumeField('contact_info.portfolio_url', v)}
                        onVisibilityChange={(v) => {
                          const newVisibility = { ...(resumeData.contact_info.visibility || {}), portfolio: v };
                          updateResumeField('contact_info.visibility', newVisibility);
                        }}
                        isLink
                      />
                    </div>
                  </div>
                )}
              </Section>

              {/* Education */}
              <Section
                title="Education"
                icon="🎓"
                isExpanded={expandedSection === 'education'}
                onToggle={() => toggleSection('education')}
              >
                {resumeData.education?.map((edu, index) => (
                  <div key={index} style={{
                    marginBottom: index < resumeData.education.length - 1 ? '20px' : 0,
                    paddingBottom: index < resumeData.education.length - 1 ? '20px' : 0,
                    borderBottom: index < resumeData.education.length - 1 ? '1px solid var(--border-light)' : 'none'
                  }}>
                    {edu.school_name && (
                      <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                        <EditableField
                          value={edu.school_name}
                          onSave={(val) => updateResumeField(`education[${index}].school_name`, val)}
                        />
                      </div>
                    )}
                    {edu.degree && (
                      <InfoRow
                        label="Degree"
                        value={edu.degree}
                        editable
                        onSave={(val) => updateResumeField(`education[${index}].degree`, val)}
                      />
                    )}
                    {edu.gpa && (
                      <InfoRow
                        label="GPA"
                        value={edu.gpa}
                        editable
                        onSave={(val) => updateResumeField(`education[${index}].gpa`, val)}
                      />
                    )}
                    {edu.major && (
                      <InfoRow
                        label="Major"
                        value={edu.major}
                        editable
                        onSave={(val) => updateResumeField(`education[${index}].major`, val)}
                      />
                    )}
                    {edu.graduation_date && (
                      <InfoRow
                        label="Graduation Date"
                        value={edu.graduation_date}
                        editable
                        onSave={(val) => updateResumeField(`education[${index}].graduation_date`, val)}
                      />
                    )}
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>Honors/Achievements:</div>
                      {edu.bullets && edu.bullets.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.6' }}>
                          {edu.bullets.map((bullet, i) => {
                            const isSelected = selectedBullet?.section === 'education' &&
                                              selectedBullet?.sectionIndex === index &&
                                              selectedBullet?.bulletIndex === i;
                            return (
                            <li key={i} style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <EditableField
                                  value={bullet}
                                  onSave={(val) => updateResumeField(`education[${index}].bullets[${i}]`, val)}
                                  isMultiline={true}
                                  isSelected={isSelected}
                                  onSelect={() => setSelectedBullet({ section: 'education', sectionIndex: index, bulletIndex: i })}
                                />
                              </div>
                              <button
                                onClick={() => deleteBulletPoint(`education[${index}].bullets`, i)}
                                style={{
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  border: '1px solid rgba(239, 68, 68, 0.3)',
                                  color: 'var(--red-500)',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  marginTop: '2px'
                                }}
                                title="Delete bullet point"
                              >
                                ✕
                              </button>
                            </li>
                            );
                          })}
                        </ul>
                      )}
                      <button
                        onClick={() => addBulletPoint(`education[${index}].bullets`)}
                        style={{
                          marginTop: '8px',
                          padding: '6px 12px',
                          background: 'rgba(147, 51, 234, 0.1)',
                          border: '1px solid rgba(99, 102, 241, 0.3)',
                          color: 'var(--indigo-500)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          width: '100%'
                        }}
                      >
                        + Add Bullet Point
                      </button>
                    </div>
                  </div>
                ))}
              </Section>

              {/* Experience */}
              <Section
                title="Experience"
                icon="💼"
                isExpanded={expandedSection === 'experience'}
                onToggle={() => toggleSection('experience')}
              >
                {resumeData.experience?.map((exp, index) => (
                  <div key={index} style={{
                    marginBottom: index < resumeData.experience.length - 1 ? '20px' : 0,
                    paddingBottom: index < resumeData.experience.length - 1 ? '20px' : 0,
                    borderBottom: index < resumeData.experience.length - 1 ? '1px solid var(--border-light)' : 'none'
                  }}>
                    {exp.job_title && (
                      <InfoRow
                        label="Job Title"
                        value={exp.job_title}
                        editable
                        onSave={(val) => updateResumeField(`experience[${index}].job_title`, val)}
                      />
                    )}
                    {exp.company_name && (
                      <InfoRow
                        label="Company"
                        value={exp.company_name}
                        editable
                        onSave={(val) => updateResumeField(`experience[${index}].company_name`, val)}
                      />
                    )}
                    {exp.location && (
                      <InfoRow
                        label="Location"
                        value={exp.location}
                        editable
                        onSave={(val) => updateResumeField(`experience[${index}].location`, val)}
                      />
                    )}
                    {exp.start_date && (
                      <InfoRow
                        label="Start Date"
                        value={exp.start_date}
                        editable
                        onSave={(val) => updateResumeField(`experience[${index}].start_date`, val)}
                      />
                    )}
                    {exp.end_date && (
                      <InfoRow
                        label="End Date"
                        value={exp.end_date}
                        editable
                        onSave={(val) => updateResumeField(`experience[${index}].end_date`, val)}
                      />
                    )}
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>Bullet Points:</div>
                      {exp.bullets && exp.bullets.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.6' }}>
                          {exp.bullets.map((bullet, i) => {
                            const isSelected = selectedBullet?.section === 'experience' &&
                                              selectedBullet?.sectionIndex === index &&
                                              selectedBullet?.bulletIndex === i;
                            return (
                            <li key={i} style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <EditableField
                                  value={bullet}
                                  onSave={(val) => updateResumeField(`experience[${index}].bullets[${i}]`, val)}
                                  isMultiline={true}
                                  isSelected={isSelected}
                                  onSelect={() => setSelectedBullet({ section: 'experience', sectionIndex: index, bulletIndex: i })}
                                />
                              </div>
                              <button
                                onClick={() => deleteBulletPoint(`experience[${index}].bullets`, i)}
                                style={{
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  border: '1px solid rgba(239, 68, 68, 0.3)',
                                  color: 'var(--red-500)',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  marginTop: '2px'
                                }}
                                title="Delete bullet point"
                              >
                                ✕
                              </button>
                            </li>
                            );
                          })}
                        </ul>
                      )}
                      <button
                        onClick={() => addBulletPoint(`experience[${index}].bullets`)}
                        style={{
                          marginTop: '8px',
                          padding: '6px 12px',
                          background: 'rgba(147, 51, 234, 0.1)',
                          border: '1px solid rgba(99, 102, 241, 0.3)',
                          color: 'var(--indigo-500)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          width: '100%'
                        }}
                      >
                        + Add Bullet Point
                      </button>
                    </div>
                  </div>
                ))}
              </Section>

              {/* Projects */}
              <Section
                title="Projects"
                icon="🚀"
                isExpanded={expandedSection === 'projects'}
                onToggle={() => toggleSection('projects')}
              >
                {resumeData.projects?.map((proj, index) => (
                  <div key={index} style={{
                    marginBottom: index < resumeData.projects.length - 1 ? '20px' : 0,
                    paddingBottom: index < resumeData.projects.length - 1 ? '20px' : 0,
                    borderBottom: index < resumeData.projects.length - 1 ? '1px solid var(--border-light)' : 'none'
                  }}>
                    {proj.project_name && (
                      <InfoRow
                        label="Project Name"
                        value={proj.project_name}
                        editable
                        onSave={(val) => updateResumeField(`projects[${index}].project_name`, val)}
                      />
                    )}
                    {proj.context && (
                      <InfoRow
                        label="Context"
                        value={proj.context}
                        editable
                        onSave={(val) => updateResumeField(`projects[${index}].context`, val)}
                      />
                    )}
                    {proj.date && (
                      <InfoRow
                        label="Date"
                        value={proj.date}
                        editable
                        onSave={(val) => updateResumeField(`projects[${index}].date`, val)}
                      />
                    )}
                    {proj.github_link && (
                      <InfoRow
                        label="GitHub"
                        value={proj.github_link}
                        isLink
                        editable
                        onSave={(val) => updateResumeField(`projects[${index}].github_link`, val)}
                      />
                    )}
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>Bullet Points:</div>
                      {proj.bullets && proj.bullets.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.6' }}>
                          {proj.bullets.map((bullet, i) => {
                            const isSelected = selectedBullet?.section === 'projects' &&
                                              selectedBullet?.sectionIndex === index &&
                                              selectedBullet?.bulletIndex === i;
                            return (
                            <li key={i} style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <EditableField
                                  value={bullet}
                                  onSave={(val) => updateResumeField(`projects[${index}].bullets[${i}]`, val)}
                                  isMultiline={true}
                                  isSelected={isSelected}
                                  onSelect={() => setSelectedBullet({ section: 'projects', sectionIndex: index, bulletIndex: i })}
                                />
                              </div>
                              <button
                                onClick={() => deleteBulletPoint(`projects[${index}].bullets`, i)}
                                style={{
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  border: '1px solid rgba(239, 68, 68, 0.3)',
                                  color: 'var(--red-500)',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  marginTop: '2px'
                                }}
                                title="Delete bullet point"
                              >
                                ✕
                              </button>
                            </li>
                            );
                          })}
                        </ul>
                      )}
                      <button
                        onClick={() => addBulletPoint(`projects[${index}].bullets`)}
                        style={{
                          marginTop: '8px',
                          padding: '6px 12px',
                          background: 'rgba(147, 51, 234, 0.1)',
                          border: '1px solid rgba(99, 102, 241, 0.3)',
                          color: 'var(--indigo-500)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          width: '100%'
                        }}
                      >
                        + Add Bullet Point
                      </button>
                    </div>
                  </div>
                ))}
              </Section>

              {/* Skills */}
              <Section
                title="Skills"
                icon="⚡"
                isExpanded={expandedSection === 'skills'}
                onToggle={() => toggleSection('skills')}
              >
                {resumeData.skills && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {resumeData.skills.languages?.length > 0 && (
                      <InfoRow
                        label="Languages"
                        value={Array.isArray(resumeData.skills.languages) ? resumeData.skills.languages.join(', ') : resumeData.skills.languages}
                        editable
                        onSave={(val) => updateResumeField('skills.languages', val.split(',').map(s => s.trim()))}
                      />
                    )}
                    {resumeData.skills.frontend?.length > 0 && (
                      <InfoRow
                        label="Frontend"
                        value={Array.isArray(resumeData.skills.frontend) ? resumeData.skills.frontend.join(', ') : resumeData.skills.frontend}
                        editable
                        onSave={(val) => updateResumeField('skills.frontend', val.split(',').map(s => s.trim()))}
                      />
                    )}
                    {resumeData.skills.backend?.length > 0 && (
                      <InfoRow
                        label="Backend"
                        value={Array.isArray(resumeData.skills.backend) ? resumeData.skills.backend.join(', ') : resumeData.skills.backend}
                        editable
                        onSave={(val) => updateResumeField('skills.backend', val.split(',').map(s => s.trim()))}
                      />
                    )}
                    {resumeData.skills.databases?.length > 0 && (
                      <InfoRow
                        label="Databases"
                        value={Array.isArray(resumeData.skills.databases) ? resumeData.skills.databases.join(', ') : resumeData.skills.databases}
                        editable
                        onSave={(val) => updateResumeField('skills.databases', val.split(',').map(s => s.trim()))}
                      />
                    )}
                    {resumeData.skills.cloud_devops?.length > 0 && (
                      <InfoRow
                        label="Cloud & DevOps"
                        value={Array.isArray(resumeData.skills.cloud_devops) ? resumeData.skills.cloud_devops.join(', ') : resumeData.skills.cloud_devops}
                        editable
                        onSave={(val) => updateResumeField('skills.cloud_devops', val.split(',').map(s => s.trim()))}
                      />
                    )}
                    {resumeData.skills.tools?.length > 0 && (
                      <InfoRow
                        label="Tools"
                        value={Array.isArray(resumeData.skills.tools) ? resumeData.skills.tools.join(', ') : resumeData.skills.tools}
                        editable
                        onSave={(val) => updateResumeField('skills.tools', val.split(',').map(s => s.trim()))}
                      />
                    )}
                  </div>
                )}
              </Section>
            </div>

            {/* Middle Panel - PDF Viewer */}
            <div style={{
              height: '100%',
              background: 'var(--bg-elevated)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              position: 'relative'
            }}>
              {pdfUrl ? (
                <>
                  <iframe
                    key={pdfUrl}
                    src={pdfUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                      opacity: isRegenerating ? 0.3 : 1,
                      transition: 'opacity 0.3s'
                    }}
                    title="Resume PDF"
                  />
                  {isRegenerating && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'rgba(0, 0, 0, 0.8)',
                      padding: '24px 32px',
                      borderRadius: '12px',
                      border: '1px solid var(--indigo-500)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      boxShadow: '0 8px 32px rgba(147, 51, 234, 0.4)'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid rgba(99, 102, 241, 0.3)',
                        borderTop: '4px solid var(--indigo-500)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '600' }}>
                        Regenerating Resume...
                      </div>
                      <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
                        Compiling LaTeX to PDF
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: 'var(--text-tertiary)' }}>Loading PDF...</div>
              )}
            </div>

            {/* Right Panel - Chatbot */}
            <ChatBot
              mode="brainstorming"
              context={{
                formData: resumeData,
                masterResume: masterResume,
                selectedBullet: selectedBullet,
                company: application.company_name,
                role: application.job_role
              }}
              onBulletUpdate={handleBulletUpdate}
            />
          </ResizablePanels>
        )}
      </div>
    </div>
  );
};

// Helper Components
const Section = ({ title, icon, isExpanded, onToggle, children }) => (
  <div style={{
    marginBottom: '16px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid var(--border-light)',
    borderRadius: '12px',
    overflow: 'hidden'
  }}>
    <button
      onClick={onToggle}
      style={{
        width: '100%',
        padding: '16px 20px',
        background: 'transparent',
        border: 'none',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '16px',
        fontWeight: '600'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      <span style={{
        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s'
      }}>
        ▼
      </span>
    </button>
    {isExpanded && (
      <div style={{
        padding: '20px',
        borderTop: '1px solid var(--border-light)'
      }}>
        {children}
      </div>
    )}
  </div>
);

const InfoRow = ({ label, value, isLink, onSave, editable = false, isChanged = false }) => (
  <div style={{
    marginBottom: '8px',
    padding: isChanged ? '4px 8px' : '0',
    background: isChanged ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
    borderRadius: isChanged ? '4px' : '0',
    border: isChanged ? '1px solid rgba(16, 185, 129, 0.3)' : 'none',
    transition: 'all 0.2s'
  }}>
    <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{label}: </span>
    {editable && onSave ? (
      <EditableField
        value={value}
        onSave={onSave}
        placeholder={label}
        type={isLink ? 'url' : 'text'}
      />
    ) : isLink ? (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: '14px', color: 'var(--indigo-500)', textDecoration: 'none' }}
      >
        {value}
      </a>
    ) : (
      <span style={{ fontSize: '14px' }}>{value}</span>
    )}
  </div>
);

const FieldWithVisibility = ({ label, value, visible, onValueChange, onVisibilityChange, isLink = false }) => (
  <div style={{ marginBottom: '12px' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
      <label style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{label}</label>
      <button
        onClick={() => onVisibilityChange(!visible)}
        style={{
          padding: '4px 10px',
          background: visible ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 107, 107, 0.2)',
          border: `1px solid ${visible ? 'rgba(34, 197, 94, 0.5)' : 'rgba(255, 107, 107, 0.5)'}`,
          borderRadius: '4px',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontSize: '11px',
          fontWeight: '600'
        }}
      >
        {visible ? '👁 Visible' : '🚫 Hidden'}
      </button>
    </div>
    <EditableField
      value={value || ''}
      onSave={onValueChange}
      placeholder={visible ? label : `${label} (hidden from PDF)`}
      type={isLink ? 'url' : 'text'}
    />
  </div>
);

export default ResumeViewer;
